import React, { FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const customerSlug = (searchParams.get('customer') || '').trim();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (!email.trim()) {
      setError('Bitte gib eine E-Mail-Adresse ein.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          email: email.trim() || null,
          password,
          ...(customerSlug ? { customerSlug } : {}),
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Registrierung fehlgeschlagen');
      }
      setMessage(
        payload.message || 'Registrierung erfolgreich eingereicht. Ein Admin oder Owner muss den Account noch freischalten.'
      );
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (submitError: any) {
      setError(submitError.message || 'Registrierung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-stone-100 px-6 py-16 text-stone-900">
      <div className="mx-auto max-w-xl rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
        <p className="text-sm uppercase tracking-[0.35em] text-stone-500">Auth</p>
        <h1 className="mt-3 text-3xl font-semibold">Registrieren</h1>
        <p className="mt-3 text-sm leading-6 text-stone-600">
          Diese Seite ist absichtlich noch nicht verlinkt. Neue Accounts werden zunächst als nicht verifiziert angelegt und müssen von einem Admin oder Owner freigeschaltet werden.
        </p>

        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">Username</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium">E-Mail</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              type="email"
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium">Passwort</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              type="password"
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium">Passwort wiederholen</span>
            <input
              className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 outline-none transition focus:border-stone-900"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              type="password"
              required
            />
          </label>

          {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <button
            className="w-full rounded-2xl bg-stone-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-stone-700 disabled:cursor-not-allowed disabled:bg-stone-400"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Registriere...' : 'Account anlegen'}
          </button>
        </form>
      </div>
    </main>
  );
};

export default RegisterPage;
